import { Category } from '@gripp/shared-logic';
import { Colors, colors, useSlideLayout } from '@gripp/shared-ui';
import { Icon } from 'libs/shared-ui/src/components/crop-circle/icons/Icon';
import { useContext, useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { FilesContext } from './filesContext';

export const FilterPanelHeaderActions = ({
  onReset,
}: {
  onReset: () => void;
}) => {
  return (
    <View style={styles.headerActions}>
      <Pressable onPress={onReset}>
        <Icon name="Reset" size={24} />
      </Pressable>
    </View>
  );
};

export const FilterPanel = () => {
  const { allFiles, selectedCategoryId, setSelectedCategoryId } =
    useContext(FilesContext);
  const { toggleThirdPanel } = useSlideLayout();

  const categories = useMemo(() => {
    const uniqueCategories = new Map<
      string,
      { category: Category; count: number }
    >();

    allFiles.forEach((file) => {
      file.categories?.forEach((category) => {
        if (category) {
          const existing = uniqueCategories.get(category.id);
          if (existing) {
            uniqueCategories.set(category.id, {
              category,
              count: existing.count + 1,
            });
          } else {
            uniqueCategories.set(category.id, { category, count: 1 });
          }
        }
      });
    });

    return Array.from(uniqueCategories.values());
  }, [allFiles]);

  const filteredCount = useMemo(() => {
    if (!selectedCategoryId) return allFiles.length;
    return allFiles.filter((file) =>
      file.categories?.some((cat) => cat.id === selectedCategoryId)
    ).length;
  }, [allFiles, selectedCategoryId]);

  return (
    <View style={styles.filterPanel}>
      <View style={styles.filterContent}>
        <Text style={styles.filterSectionTitle}>Categories</Text>
        <View style={styles.filterCategoryList}>
          <Pressable
            style={[
              styles.filterCategoryBadge,
              !selectedCategoryId && styles.filterCategoryBadgeSelected,
            ]}
            onPress={() => setSelectedCategoryId(undefined)}
          >
            <Text
              style={[
                styles.filterCategoryText,
                !selectedCategoryId && styles.filterCategoryTextSelected,
              ]}
            >
              All
            </Text>
          </Pressable>
          {categories.map(({ category }) => (
            <Pressable
              key={category.id}
              style={[
                styles.filterCategoryBadge,
                selectedCategoryId === category.id &&
                  styles.filterCategoryBadgeSelected,
              ]}
              onPress={() => setSelectedCategoryId(category.id)}
            >
              <Text
                style={[
                  styles.filterCategoryText,
                  selectedCategoryId === category.id &&
                    styles.filterCategoryTextSelected,
                ]}
              >
                {category.name}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>
      <Pressable style={styles.viewResultsButton} onPress={toggleThirdPanel}>
        <Text style={styles.viewResultsText}>View {filteredCount} results</Text>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  filterPanel: {
    flex: 1,
    backgroundColor: Colors.role.canvas,
    padding: 16,
    justifyContent: 'space-between',
  },
  filterContent: {
    flex: 1,
  },
  viewResultsButton: {
    height: 48,
    borderRadius: 24,
    backgroundColor: Colors.role.surfaceHover,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  viewResultsText: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.role.textStrong,
  },
  filterSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.role.textStrong,
    marginBottom: 16,
  },
  filterCategoryList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterCategoryBadge: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: Colors.role.surfaceHover,
  },
  filterCategoryBadgeSelected: {
    backgroundColor: Colors.role.surfaceInverse,
  },
  filterCategoryText: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.role.textStrong,
  },
  filterCategoryTextSelected: {
    color: Colors.role.textOnInverse,
  },
});
